import { ColorType, GrIconType } from "@jnpmedi/grebob";
import { isMobileDevice, MvnMessage, resolveHostOSByUA, TextFieldFormErrorType } from "@newLibs/utils";
import icoDocument from "@assets/icons/ico-document.png";
import icoFolder from "@assets/icons/ico-folder.png";
import icoMsExcel from "@assets/icons/ico-ms-excel.png";
import icoMsPowerPoint from "@assets/icons/ico-ms-powerpoint.png";
import icoMsWord from "@assets/icons/ico-ms-word.png";
import icoSignDocument from "@assets/icons/ico-sign-document.png";
import icoVersions from "@assets/icons/ico-versions.png";
import { DOCS_DOCUMENT_LANG } from "@maven-msg/lang/docs";
import { MvnLanguage } from "@maven-msg/lib/Message";
import { LoLang } from "@maven-rest/common/common.types";
import { DocumentContentType } from "@maven-rest/docs/Document.dto";
import {
  DocumentSignerAttrType,
  DocumentSignerHistory,
  DocumentSignerHistoryType,
  DocumentSignerStatus,
} from "@maven-rest/docs/DocumentSign.dto";
import { BrowseEntrySummary, BrowseEntryType, FolderType } from "@maven-rest/docs/Folder.dto";
import { ReadableFileExt } from "@maven-rest/docs/FolderFile.conv";
import { UserEssential } from "@maven-rest/sso/User.dto";

export enum DocumentFileExt {
  Bmp = "bmp",
  Doc = "doc",
  Docx = "docx",
  Jpeg = "jpeg",
  Pdf = "pdf",
  Png = "png",
  Ppt = "ppt",
  Pptx = "pptx",
  Tif = "tif",
  Tiff = "tiff",
  Txt = "txt",
  Xls = "xls",
  Xlsx = "xlsx",
  Xml = "xml",
  Zip = "zip",
  Eml = "eml",
  Unknown = "unknown",
}

export function convertFileExtToGrIconType(browseEntrySummary?: BrowseEntrySummary): GrIconType {
  if (!browseEntrySummary) return "visual-unknown";

  const { type, resource } = browseEntrySummary;
  switch (type) {
    case BrowseEntryType.Folder:
      return resource.folderType === FolderType.ECONSENT ? "visual-book" : "visual-folder";
    case BrowseEntryType.Proposal:
      return "visual-scope";
    case BrowseEntryType.Sign:
      return "visual-file-signature";
    case BrowseEntryType.Unknown:
      return "visual-unknown";
    case BrowseEntryType.Document: {
      const { contentType } = browseEntrySummary.document;
      return convertContentTypeToGrIconType(contentType);
    }
    default:
      return "visual-unknown";
  }
}

export function convertContentTypeToGrIconType(contentType: DocumentContentType): GrIconType {
  switch (contentType) {
    case DocumentContentType.DocsPdf:
    case DocumentContentType.PDF:
      return "visual-pdf";
    case DocumentContentType.MsExcel:
      return "visual-xls";
    case DocumentContentType.MsPowerPoint:
      return "visual-ppt";
    case DocumentContentType.MsWord:
      return "visual-word";
    case DocumentContentType.Text:
      return "visual-text";
    case DocumentContentType.Proposal:
      return "visual-task-add";
    default:
      return "visual-unknown";
  }
}

export function readableKBFileSize(size: number) {
  const mbOrMib = resolveHostOSByUA() === "Windows" ? 1024 : 1000;
  if (!size) return "";
  else if (size < mbOrMib) return `${size} KB`;
  else if (size < mbOrMib * mbOrMib) return `${Math.ceil((size / mbOrMib) * 10) / 10} MB`;
  else return `${Math.ceil((size / mbOrMib / mbOrMib) * 10) / 10} MB`;
}

export function readableVersion(browseEntrySummary: BrowseEntrySummary): string {
  const { type } = browseEntrySummary;

  return type === BrowseEntryType.Folder
    ? ""
    : type === BrowseEntryType.Proposal
    ? browseEntrySummary.proposal.versions.length
      ? `${browseEntrySummary.proposal.versions[0].version}${
          browseEntrySummary.proposal.versions.length > 1 ? ` (${browseEntrySummary.proposal.versions.length - 1})` : ""
        }`
      : "-"
    : type === BrowseEntryType.Document
    ? browseEntrySummary.document.version
    : type === BrowseEntryType.Sign
    ? browseEntrySummary.sign.version
    : "-";
}

export function readableKind(browseEntrySummary: BrowseEntrySummary) {
  const { type, resource } = browseEntrySummary;

  return type === BrowseEntryType.Folder
    ? resource.folderType === FolderType.ECONSENT
      ? "Study"
      : "Folder"
    : type === BrowseEntryType.Document
    ? browseEntrySummary.document.contentType === DocumentContentType.PDF
      ? "PDF Document"
      : browseEntrySummary.document.contentType === DocumentContentType.DocsPdf
      ? "PDF Document"
      : browseEntrySummary.document.contentType === DocumentContentType.MsWord
      ? "MS Word"
      : browseEntrySummary.document.contentType === DocumentContentType.MsExcel
      ? "MS Excel"
      : browseEntrySummary.document.contentType === DocumentContentType.MsPowerPoint
      ? "MS PowerPoint"
      : "Unknown"
    : type === BrowseEntryType.Proposal
    ? "Composer Proposal"
    : type === BrowseEntryType.Sign
    ? resource.folderType === FolderType.ECONSENT
      ? "ICF"
      : "e-Sign Document"
    : "Unknown";
}

export function readableCreatedBy(userEssential: UserEssential) {
  return userEssential.name ? `${userEssential.name} (${userEssential.email})` : `${userEssential.email || "-"}`;
}

export function convertFileTypeToGrIconType(mime?: string): GrIconType {
  switch (mime) {
    case "application/vnd.ms-powerpoint":
    case "application/vnd.openxmlformats-officedocument.presentationml.presentation":
      return "visual-ppt";
    case "application/pdf":
      return "visual-pdf";
    case "application/vnd.ms-excel":
    case "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
      return "visual-xls";
    case "image/png":
    case "image/jpeg":
      return "visual-image";
    default:
      return "visual-document";
  }
}

export const isInvalidFileType = (file: File, accept: string): boolean => {
  const fileType = String(file.type).toLowerCase();
  const accepts = String(accept).split("|").join(",").split(",");
  let valid: boolean = true;

  for (const acceptType of accepts) {
    // 필요할 경우 npmjs/mime 추가하여 mime-ext 대응
    if (
      acceptType.toLowerCase() === fileType ||
      (acceptType === ".png" && fileType === "image/png") ||
      (acceptType === ".jpg" && fileType === "image/jpeg") ||
      (acceptType === ".pdf" && fileType === "application/pdf") ||
      (acceptType === ".doc" && fileType === "application/msword") ||
      (acceptType === ".ppt" && fileType === "application/vnd.ms-powerpoint") ||
      (acceptType === ".xls" && fileType === "application/vnd.ms-excel") ||
      (acceptType === ".docx" && fileType === "application/vnd.openxmlformats-officedocument.wordprocessingml.document") ||
      (acceptType === ".pptx" && fileType === "application/vnd.openxmlformats-officedocument.presentationml.presentation") ||
      (acceptType === ".xlsx" && fileType === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet") ||
      (acceptType === ".xlsx" && fileType === "application/haansoftxlsx") ||
      (acceptType === ".xlsx" && fileType === "application/vnd.ms-excel.12") ||
      (acceptType === ".xml" && fileType === "application/xml") ||
      (acceptType === ".xml" && fileType === "text/xml") ||
      (acceptType === ".zip" && fileType === "application/zip") ||
      (acceptType === ".zip" && fileType === "application/x-compress") ||
      (acceptType === ".zip" && fileType === "application/x-compressed") ||
      (acceptType === ".zip" && fileType === "application/x-zip-compressed") ||
      (acceptType === ".zip" && fileType === "multipart/x-zip") ||
      (acceptType === ".dcm" && fileType === "application/dicom") ||
      (acceptType === ".bmp" && fileType === "image/bmp") ||
      (acceptType === ".bmp" && fileType === "image/x-ms-bmp") ||
      (acceptType === ".txt" && fileType === "text/plain") ||
      (acceptType === ".csv" && fileType === "text/csv")
    ) {
      valid = false;
      break;
    }
  }

  return valid;
};

export const handleDownloadUrlResponse = (downLoadUrl: string, fileName?: string) => {
  const link = document.createElement("a");
  link.href = downLoadUrl;
  link.hidden = true;
  link.target = isMobileDevice() ? "_self" : "_blank";
  // NOTE: base64 string을 새 탭으로 열어 다운로드를 진행할 때 이름을 지정해주지 않으면 다운로드 진행이 안되는 현상으로 추가 (2025 11/2 예정된 PRD 배포 이전까지 Side effect 파악 후 제거 or 현상유지 결정예정ㅏ
  link.download = fileName || "";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export function resolveLeftPadding(history: DocumentSignerHistory): string | undefined {
  switch (history.type) {
    case DocumentSignerHistoryType.Opened:
    case DocumentSignerHistoryType.Signed:
    case DocumentSignerHistoryType.Declined:
      return "20px";
    case DocumentSignerHistoryType.SignScheduled:
    case DocumentSignerHistoryType.SignStart:
    case DocumentSignerHistoryType.SignEnd:
    case DocumentSignerHistoryType.SignDecline:
    case DocumentSignerHistoryType.SignCancel:
    case DocumentSignerHistoryType.SignExpired:
    case DocumentSignerHistoryType.Delivered:
    case DocumentSignerHistoryType.SmsDelivered:
    case DocumentSignerHistoryType.Canceled:
    default:
      return undefined;
  }
}

export function resolveDocumentSignStatusBulletIconColor(history: DocumentSignerHistory): ColorType | undefined {
  switch (history.type) {
    case DocumentSignerHistoryType.SignScheduled:
    case DocumentSignerHistoryType.SignStart:
    case DocumentSignerHistoryType.Delivered:
    case DocumentSignerHistoryType.SmsDelivered:
      return "grey040";
    case DocumentSignerHistoryType.Opened:
      return "maven-blue-dark";
    case DocumentSignerHistoryType.SignEnd:
    case DocumentSignerHistoryType.Signed:
      return "positive-primary";
    case DocumentSignerHistoryType.Canceled:
    case DocumentSignerHistoryType.Declined:
    case DocumentSignerHistoryType.SignCancel:
    case DocumentSignerHistoryType.SignDecline:
    case DocumentSignerHistoryType.SignExpired:
      return "negative-primary";
    default:
      return undefined;
  }
}

export function resolveDocumentSignerStatusBulletIconColor(status: DocumentSignerStatus): ColorType | undefined {
  switch (status) {
    case DocumentSignerStatus.None:
    case DocumentSignerStatus.Wait:
    case DocumentSignerStatus.Delivering:
    case DocumentSignerStatus.DeliveringFailed:
    case DocumentSignerStatus.Delivered:
    case DocumentSignerStatus.DeliveringSkipped:
      return "grey040";
    case DocumentSignerStatus.Opened:
      return "maven-blue-dark";
    case DocumentSignerStatus.Signed:
      return "positive-primary";
    case DocumentSignerStatus.Declined:
      return "negative-primary";
    default:
      return undefined;
  }
}

export function resolvePrefix(historyType: DocumentSignerHistoryType) {
  switch (historyType) {
    case DocumentSignerHistoryType.SignStart:
      return DOCS_DOCUMENT_LANG.DOCUMENT_SIGN_HISTORY_SIGN_START_PREFIX;
    case DocumentSignerHistoryType.SignEnd:
      return DOCS_DOCUMENT_LANG.DOCUMENT_SIGN_HISTORY_SIGN_END_PREFIX;
    case DocumentSignerHistoryType.SignDecline:
      return DOCS_DOCUMENT_LANG.DOCUMENT_SIGN_HISTORY_SIGN_DECLINE_PREFIX;
    case DocumentSignerHistoryType.SignCancel:
      return DOCS_DOCUMENT_LANG.DOCUMENT_SIGN_HISTORY_SIGN_CANCEL_PREFIX;
    case DocumentSignerHistoryType.SignExpired:
      return DOCS_DOCUMENT_LANG.DOCUMENT_SIGN_HISTORY_SIGN_EXPIRED_PREFIX;
    case DocumentSignerHistoryType.Delivered:
    case DocumentSignerHistoryType.SmsDelivered:
      return historyType === DocumentSignerHistoryType.Delivered
        ? DOCS_DOCUMENT_LANG.DOCUMENT_SIGN_HISTORY_DELIVERED_PREFIX
        : DOCS_DOCUMENT_LANG.DOCUMENT_SIGN_HISTORY_REMIND_PREFIX;
    case DocumentSignerHistoryType.Opened:
      return DOCS_DOCUMENT_LANG.DOCUMENT_SIGN_HISTORY_OPENED_PREFIX;
    case DocumentSignerHistoryType.ReferrerOpened:
      return DOCS_DOCUMENT_LANG.DOCUMENT_SIGN_HISTORY_REFERRER_OPENED_PREFIX;
    case DocumentSignerHistoryType.Signed:
      return DOCS_DOCUMENT_LANG.DOCUMENT_SIGN_HISTORY_SIGNED_PREFIX;
    case DocumentSignerHistoryType.Canceled:
      return DOCS_DOCUMENT_LANG.DOCUMENT_SIGN_HISTORY_CANCELED_PREFIX;
    case DocumentSignerHistoryType.Declined:
      return DOCS_DOCUMENT_LANG.DOCUMENT_SIGN_HISTORY_DECLINED_PREFIX;
    default:
      return "--";
  }
}

export function resolveSuffix(historyType: DocumentSignerHistoryType) {
  switch (historyType) {
    case DocumentSignerHistoryType.SignStart:
      return DOCS_DOCUMENT_LANG.DOCUMENT_SIGN_HISTORY_SIGN_START_SUFFIX;
    case DocumentSignerHistoryType.SignEnd:
      return DOCS_DOCUMENT_LANG.DOCUMENT_SIGN_HISTORY_SIGN_END_SUFFIX;
    case DocumentSignerHistoryType.SignDecline:
      return DOCS_DOCUMENT_LANG.DOCUMENT_SIGN_HISTORY_SIGN_DECLINE_SUFFIX;
    case DocumentSignerHistoryType.SignCancel:
      return DOCS_DOCUMENT_LANG.DOCUMENT_SIGN_HISTORY_SIGN_CANCEL_SUFFIX;
    case DocumentSignerHistoryType.SignExpired:
      return DOCS_DOCUMENT_LANG.DOCUMENT_SIGN_HISTORY_SIGN_EXPIRED_SUFFIX;
    case DocumentSignerHistoryType.Delivered:
    case DocumentSignerHistoryType.SmsDelivered:
      return historyType === DocumentSignerHistoryType.Delivered
        ? DOCS_DOCUMENT_LANG.DOCUMENT_SIGN_HISTORY_DELIVERED_SUFFIX
        : DOCS_DOCUMENT_LANG.DOCUMENT_SIGN_HISTORY_REMIND_SUFFIX;
    case DocumentSignerHistoryType.Opened:
      return DOCS_DOCUMENT_LANG.DOCUMENT_SIGN_HISTORY_OPENED_SUFFIX;
    case DocumentSignerHistoryType.ReferrerOpened:
      return DOCS_DOCUMENT_LANG.DOCUMENT_SIGN_HISTORY_REFERRER_OPENED_SUFFIX;
    case DocumentSignerHistoryType.Signed:
      return DOCS_DOCUMENT_LANG.DOCUMENT_SIGN_HISTORY_SIGNED_SUFFIX;
    case DocumentSignerHistoryType.Canceled:
      return DOCS_DOCUMENT_LANG.DOCUMENT_SIGN_HISTORY_CANCELED_SUFFIX;
    case DocumentSignerHistoryType.Declined:
      return DOCS_DOCUMENT_LANG.DOCUMENT_SIGN_HISTORY_DECLINED_SUFFIX;
    default:
      return "--";
  }
}

export function resolveFolderFileIcon(entry: BrowseEntrySummary): string {
  const resolveDocumentTypeSrc = (documentContentType: DocumentContentType): string => {
    if (documentContentType === DocumentContentType.MsWord) return icoMsWord;
    else if (documentContentType === DocumentContentType.MsExcel) return icoMsExcel;
    else if (documentContentType === DocumentContentType.MsPowerPoint) return icoMsPowerPoint;
    else if ([DocumentContentType.DocsPdf, DocumentContentType.PDF].includes(documentContentType)) return icoDocument;
    else return "";
  };

  const iconSwitch = ((entry: BrowseEntrySummary) => {
    switch (entry.type) {
      case BrowseEntryType.Folder:
        return icoFolder;
      case BrowseEntryType.Proposal:
        return icoVersions;
      case BrowseEntryType.Sign:
        return icoSignDocument;
      case BrowseEntryType.Document:
        return resolveDocumentTypeSrc(entry.document.contentType);
      case BrowseEntryType.Unknown:
      default:
        return "";
    }
  })(entry);

  return iconSwitch;
}

export const convertToAttrIcon = (attrName?: DocumentSignerAttrType): GrIconType => {
  switch (attrName) {
    case DocumentSignerAttrType.Name:
      return "user";
    case DocumentSignerAttrType.Signature:
    case DocumentSignerAttrType.AgentSignature:
      return "sign";
    case DocumentSignerAttrType.Date:
      return "calendar";
    case DocumentSignerAttrType.Email:
      return "mail";
    case DocumentSignerAttrType.Title:
      return "briefcase";
    case DocumentSignerAttrType.Company:
      return "building";
    case DocumentSignerAttrType.TemplateIndex:
      return "template";
    case DocumentSignerAttrType.InputItemIndex:
    case DocumentSignerAttrType.AgentInputItemIndex:
      return "text";
    case DocumentSignerAttrType.DateItemIndex:
      return "calendar-cog";
    case DocumentSignerAttrType.ResponsiveItemIndex:
    case DocumentSignerAttrType.ResponsiveItemFieldIndex:
    case DocumentSignerAttrType.AgentResponsiveItemIndex:
    case DocumentSignerAttrType.AgentResponsiveItemFieldIndex:
      return "circle-radio";

    case DocumentSignerAttrType.AddressItemIndex:
    case DocumentSignerAttrType.AgentAddressItemIndex:
      return "flag";

    case DocumentSignerAttrType.FollowWritingItemIndex:
    case DocumentSignerAttrType.AgentFollowWritingItemIndex:
      return "pencil";

    case DocumentSignerAttrType.DropdownItemIndex:
    case DocumentSignerAttrType.AgentDropdownItemIndex:
      return "list-bullet";

    case DocumentSignerAttrType.AgentAvailableMark:
      return "rectangle-check";

    default:
      return "layout-list";
  }
};

export function resolveTextFieldFormHelperText(lang: LoLang, type?: TextFieldFormErrorType): string | undefined {
  if (!type) {
    return undefined;
  }

  const resolvedMvnLanguage = lang === LoLang.KO ? MvnLanguage.KO : MvnLanguage.EN;

  switch (type) {
    case "INVALID":
      return MvnMessage(DOCS_DOCUMENT_LANG.FORM_INVALID, undefined, undefined, resolvedMvnLanguage);
    case "WARNING":
      return MvnMessage(DOCS_DOCUMENT_LANG.FORM_REQUIRED, undefined, undefined, resolvedMvnLanguage);
    case "EMAIL_INCLUDE_KOREAN":
      return MvnMessage(DOCS_DOCUMENT_LANG.FORM_EMAIL_CONTAIN_KOREAN, undefined, undefined, resolvedMvnLanguage);
    default:
      throw new Error("Unexpected flow. type is not supported");
  }
}

/**
 * 전화번호 형식 변환
 * @param value 전화번호
 * @returns 전화번호 형식
 * @example
 * formatPhoneNumber("01012345678") => "010-1234-5678"
 */
export function formatPhoneNumber(value: string): string {
  if (value.startsWith("02")) {
    if (value.length <= 2) return value;
    if (value.length <= 6) return `${value.slice(0, 2)}-${value.slice(2)}`;
    return `${value.slice(0, 2)}-${value.slice(2, 5)}-${value.slice(5)}`;
  }

  if (value.length <= 3) return value;
  if (value.length <= 6) return `${value.slice(0, 3)}-${value.slice(3)}`;
  if (value.length <= 8 && value[0] !== "0") return `${value.slice(0, 4)}-${value.slice(4)}`;
  if (value.length <= 10) return `${value.slice(0, 3)}-${value.slice(3, 6)}-${value.slice(6)}`;
  return `${value.slice(0, 3)}-${value.slice(3, 7)}-${value.slice(7, 11)}`;
}

export function getDocsFileIconName(mime: string) {
  const fileExt = ReadableFileExt(mime).toLowerCase();

  switch (fileExt) {
    case DocumentFileExt.Pdf:
      return "pdf";

    case DocumentFileExt.Ppt:
    case DocumentFileExt.Pptx:
      return "ppt";

    case DocumentFileExt.Xls:
    case DocumentFileExt.Xlsx:
      return "xls";

    case DocumentFileExt.Zip:
      return "zip";

    case DocumentFileExt.Bmp:
    case DocumentFileExt.Jpeg:
    case DocumentFileExt.Png:
    case DocumentFileExt.Tiff:
    case DocumentFileExt.Tif:
      return "image";

    case DocumentFileExt.Doc:
    case DocumentFileExt.Docx:
    case DocumentFileExt.Txt:
    case DocumentFileExt.Xml:
    case DocumentFileExt.Unknown:
    default:
      return "document";
  }
}
